import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get, push, remove, update } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import {
  BookOpen,
  Users,
  GraduationCap,
  Newspaper,
  Plus,
  X,
  MessageCircle,
  MapPin,
  Star,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Search,
  Building2,
  Lock,
  UserPlus,
  Crown,
  Trash2,
  ArrowLeft,
} from "lucide-react";

// ─── Constants ───────────────────────────────────────────────────────────────

const EXAM_FILTERS = ["All", "MPSC", "UPSC", "Banking", "SSC", "Railway", "NDA/CDS", "State PSC"];

const LANGUAGE_FILTERS = [
  { label: "🌐 All", value: "All" },
  { label: "🟠 Marathi", value: "Marathi" },
  { label: "🔵 English", value: "English" },
  { label: "🟢 Hindi", value: "Hindi" },
];

const MATERIAL_TYPES = ["Book", "Notes", "PYQ", "Coaching Material", "Current Affairs"];
const EXAM_TYPES = ["MPSC", "UPSC", "Banking", "SSC", "Railway", "NDA/CDS", "State PSC"];
const SUBJECTS = [
  "Polity", "History", "Geography", "Economy", "Science",
  "Reasoning", "English", "Marathi", "Current Affairs", "Math", "Other",
];
const LANGUAGES = ["Marathi", "English", "Hindi"];
const CONDITIONS = ["New", "Good", "Used"];
const SCHEDULES = ["Morning 6-10am", "Evening 6-10pm", "Night 10pm-2am", "Flexible"];
const MENTOR_SUBJECTS = [
  "Polity", "History", "Geography", "Economy", "Science",
  "Reasoning", "Current Affairs", "Language Papers", "Essay", "Interview Prep",
];
const MENTOR_MODES = ["Online", "Offline", "Both"];

// Room Sharing constants
const ROOM_TYPES = ["PG", "Flat", "Hostel", "Single Room", "Shared Room"];
const ROOM_CITIES = ["Pune", "Mumbai", "Nashik", "Aurangabad", "Nagpur", "Delhi", "Hyderabad", "Bangalore", "Other"];
const FURNISHED_OPTIONS = ["Furnished", "Semi-Furnished", "Unfurnished"];

// ─── Helper functions ─────────────────────────────────────────────────────────

function getMaterialEmoji(type) {
  const map = { Book: "📚", Notes: "📝", PYQ: "📄", "Coaching Material": "🎓", "Current Affairs": "📰" };
  return map[type] || "📦";
}

function getMaterialBadgeClass(type) {
  const map = {
    Book: "bg-blue-100 text-blue-700",
    Notes: "bg-green-100 text-green-700",
    PYQ: "bg-purple-100 text-purple-700",
    "Coaching Material": "bg-orange-100 text-orange-700",
    "Current Affairs": "bg-red-100 text-red-700",
  };
  return map[type] || "bg-gray-100 text-gray-700";
}

function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatPrice(price) {
  const n = Number(price);
  if (!n || n === 0) return "Free";
  return `₹${n}`;
}

function openWhatsApp(contact) {
  const cleaned = String(contact).replace(/\D/g, "").slice(-10);
  window.open(`https://wa.me/91${cleaned}`, "_blank");
}

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ExamBadge({ exam }) {
  const colors = {
    MPSC: "bg-orange-100 text-orange-700",
    UPSC: "bg-red-100 text-red-700",
    Banking: "bg-blue-100 text-blue-700",
    SSC: "bg-green-100 text-green-700",
    Railway: "bg-yellow-100 text-yellow-700",
    "NDA/CDS": "bg-indigo-100 text-indigo-700",
    "State PSC": "bg-pink-100 text-pink-700",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors[exam] || "bg-gray-100 text-gray-700"}`}>
      {exam}
    </span>
  );
}

function LangBadge({ lang }) {
  const colors = {
    Marathi: "bg-orange-50 text-orange-600 border border-orange-200",
    English: "bg-blue-50 text-blue-600 border border-blue-200",
    Hindi: "bg-green-50 text-green-600 border border-green-200",
  };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors[lang] || "bg-gray-50 text-gray-600 border border-gray-200"}`}>
      {lang}
    </span>
  );
}

function StarRating({ rating }) {
  if (!rating) return null;
  const r = Math.round(Number(rating) * 2) / 2;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          size={12}
          className={s <= r ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}
        />
      ))}
      <span className="text-xs text-gray-500 ml-1">{Number(rating).toFixed(1)}</span>
    </div>
  );
}

// ─── Material Card ────────────────────────────────────────────────────────────

function MaterialCard({ item, currentUserId, onDelete, isAdmin }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3">
      {/* Top: image / emoji + badge */}
      <div className="flex items-start gap-3">
        {item.imageUrl ? (
          <img
            src={item.imageUrl}
            alt={item.title}
            className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center text-3xl flex-shrink-0">
            {getMaterialEmoji(item.materialType)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-1 mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getMaterialBadgeClass(item.materialType)}`}>
              {item.materialType}
            </span>
            {item.examType && <ExamBadge exam={item.examType} />}
            {item.language && <LangBadge lang={item.language} />}
          </div>
          <h3 className="font-semibold text-gray-900 text-sm leading-tight line-clamp-2">{item.title}</h3>
          {item.subject && (
            <p className="text-xs text-gray-500 mt-0.5">{item.subject}</p>
          )}
        </div>
        {(isAdmin || (currentUserId && item.postedBy === currentUserId)) && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
            title="Delete listing"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Description */}
      {item.description && (
        <p className="text-xs text-gray-600 line-clamp-2">{item.description}</p>
      )}

      {/* Condition + Price */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {item.condition && (
            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {item.condition}
            </span>
          )}
          <span className={`text-sm font-bold ${Number(item.price) === 0 ? "text-green-600" : "text-blue-700"}`}>
            {formatPrice(item.price)}
          </span>
        </div>
        <span className="text-xs text-gray-400">{timeAgo(item.createdAt)}</span>
      </div>

      {/* Seller info + CTA */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
            {getInitials(item.postedByName)}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">{item.postedByName || "Anonymous"}</p>
            {item.college && (
              <p className="text-xs text-gray-400 truncate flex items-center gap-0.5">
                <MapPin size={10} />{item.college}
              </p>
            )}
          </div>
        </div>
        {item.contact && (
          <button
            onClick={() => openWhatsApp(item.contact)}
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors flex-shrink-0"
          >
            <MessageCircle size={13} />
            WhatsApp
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Study Group Card ─────────────────────────────────────────────────────────

function StudyGroupCard({ item, currentUserId, onDelete, isAdmin }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap gap-1">
          {item.examType && <ExamBadge exam={item.examType} />}
          {item.language && <LangBadge lang={item.language} />}
          {item.schedule && (
            <span className="text-xs bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded-full">
              {item.schedule}
            </span>
          )}
        </div>
        {(isAdmin || (currentUserId && item.postedBy === currentUserId)) && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Location + members */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {item.location && (
          <span className="flex items-center gap-1">
            <MapPin size={12} className="text-orange-400" />
            {item.location}
          </span>
        )}
        {item.membersNeeded && (
          <span className="flex items-center gap-1">
            <Users size={12} className="text-blue-400" />
            {item.membersNeeded} member{Number(item.membersNeeded) > 1 ? "s" : ""} needed
          </span>
        )}
      </div>

      {/* Description */}
      {item.description && (
        <p className="text-sm text-gray-700 line-clamp-3">{item.description}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
        <div className="flex items-center gap-1.5">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
            {getInitials(item.postedByName)}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-700">{item.postedByName || "Anonymous"}</p>
            <p className="text-xs text-gray-400">{timeAgo(item.createdAt)}</p>
          </div>
        </div>
        {item.contact && (
          <button
            onClick={() => openWhatsApp(item.contact)}
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors"
          >
            <MessageCircle size={13} />
            Join Group
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Mentor Card ──────────────────────────────────────────────────────────────

function MentorCard({ item, currentUserId, onDelete, isAdmin }) {
  const modeBadge = (mode) => {
    const map = {
      Online: "bg-blue-100 text-blue-700",
      Offline: "bg-green-100 text-green-700",
      Both: "bg-purple-100 text-purple-700",
    };
    return map[mode] || "bg-gray-100 text-gray-700";
  };

  const subjectsArr = Array.isArray(item.subjects)
    ? item.subjects
    : typeof item.subjects === "string"
    ? item.subjects.split(",").map((s) => s.trim())
    : [];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3">
      {/* Top */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="w-14 h-14 rounded-full border-2 border-yellow-400 bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white text-lg font-bold flex-shrink-0 shadow-sm">
          {getInitials(item.name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">{item.name || item.postedByName}</h3>
              {item.examType && (
                <p className="text-xs text-gray-500">
                  Cleared <span className="font-semibold text-orange-600">{item.examType}</span>
                  {item.yearCleared ? ` (${item.yearCleared})` : ""}
                </p>
              )}
            </div>
            {(isAdmin || (currentUserId && item.postedBy === currentUserId)) && (
              <button
                onClick={() => onDelete(item.id)}
                className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <StarRating rating={item.rating} />
        </div>
      </div>

      {/* Mode + Price */}
      <div className="flex items-center gap-2 flex-wrap">
        {item.mode && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${modeBadge(item.mode)}`}>
            {item.mode}
          </span>
        )}
        <span className={`text-sm font-bold ${Number(item.pricePerSession) === 0 ? "text-green-600" : "text-blue-700"}`}>
          {Number(item.pricePerSession) === 0 ? "Free" : `₹${item.pricePerSession}/session`}
        </span>
      </div>

      {/* Subjects */}
      {subjectsArr.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {subjectsArr.slice(0, 6).map((s) => (
            <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
              {s}
            </span>
          ))}
          {subjectsArr.length > 6 && (
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
              +{subjectsArr.length - 6} more
            </span>
          )}
        </div>
      )}

      {/* Bio */}
      {item.bio && (
        <p className="text-xs text-gray-600 line-clamp-3 bg-gray-50 rounded-xl p-2.5">{item.bio}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
        <div className="flex items-center gap-1">
          <ShieldCheck size={13} className="text-green-500" />
          <span className="text-xs text-gray-500">Verified Mentor</span>
        </div>
        {item.contact && (
          <button
            onClick={() => openWhatsApp(item.contact)}
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors"
          >
            <MessageCircle size={13} />
            Contact
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Current Affairs Card ─────────────────────────────────────────────────────

function CurrentAffairsCard({ item, currentUserId, onDelete, isAdmin }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-xl flex-shrink-0">
            📰
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-sm leading-tight">{item.title}</h3>
            <div className="flex flex-wrap gap-1 mt-1">
              {item.examType && <ExamBadge exam={item.examType} />}
              {item.language && <LangBadge lang={item.language} />}
            </div>
          </div>
        </div>
        {(isAdmin || (currentUserId && item.postedBy === currentUserId)) && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {item.description && (
        <p className="text-sm text-gray-600 line-clamp-3">{item.description}</p>
      )}

      <div className="flex items-center justify-between pt-1 border-t border-gray-50">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${Number(item.price) === 0 ? "text-green-600" : "text-blue-700"}`}>
            {formatPrice(item.price)}
          </span>
          <span className="text-xs text-gray-400">{timeAgo(item.createdAt)}</span>
        </div>
        {item.contact && (
          <button
            onClick={() => openWhatsApp(item.contact)}
            className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl transition-colors"
          >
            <MessageCircle size={13} />
            Get It
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ emoji, title, subtitle, onPost, postLabel }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="text-5xl mb-4">{emoji}</div>
      <h3 className="text-lg font-bold text-gray-700 mb-1">{title}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">{subtitle}</p>
      {onPost && (
        <button
          onClick={onPost}
          className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold px-5 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all"
        >
          <Plus size={16} />
          {postLabel}
        </button>
      )}
    </div>
  );
}

// ─── Form Field Helpers ───────────────────────────────────────────────────────

function FormField({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white";
const selectCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white appearance-none";

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ExamPrepZone() {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const navigate = useNavigate();

  // Admin & aspirant access
  const isAdmin = userProfile?.isAdmin === true;
  const isAspirant = isAdmin || userProfile?.examAspirant === true;

  // Join gate state
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinForm, setJoinForm] = useState({ targetExam: "MPSC", targetYear: String(new Date().getFullYear() + 1), city: "Pune", language: "Marathi" });

  // Filters
  const [activeExam, setActiveExam] = useState("All");
  const [activeLanguage, setActiveLanguage] = useState("All");
  const [activeTab, setActiveTab] = useState("material");
  const [searchQuery, setSearchQuery] = useState("");

  // Data
  const [examListings, setExamListings] = useState([]);
  const [studyGroups, setStudyGroups] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Post form
  const [showPostForm, setShowPostForm] = useState(false);
  const [posting, setPosting] = useState(false);

  // Room sharing form
  const [roomForm, setRoomForm] = useState({ city: "Pune", area: "", rent: "", type: "PG", furnished: "Furnished", roommates: "1", description: "", contact: "" });

  // Material post form
  const [materialForm, setMaterialForm] = useState({
    materialType: "Book",
    title: "",
    examType: "MPSC",
    subject: "Polity",
    language: "Marathi",
    price: "",
    description: "",
    contact: "",
    condition: "Good",
  });

  // Study group post form
  const [groupForm, setGroupForm] = useState({
    examType: "MPSC",
    language: "Marathi",
    location: "",
    schedule: "Flexible",
    membersNeeded: "2",
    description: "",
    contact: "",
  });

  // Mentor post form
  const [mentorForm, setMentorForm] = useState({
    examType: "MPSC",
    yearCleared: "",
    subjects: [],
    mode: "Online",
    pricePerSession: "",
    bio: "",
    contact: "",
  });

  // ─── Fetch data ─────────────────────────────────────────────────────────────

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [listSnap, groupSnap, mentorSnap, roomSnap] = await Promise.all([
        get(ref(db, "examListings")),
        get(ref(db, "studyGroups")),
        get(ref(db, "mentors")),
        get(ref(db, "examRoomSharing")),
      ]);

      const toArray = (snap) =>
        snap.exists()
          ? Object.entries(snap.val()).map(([id, val]) => ({ id, ...val }))
          : [];

      setExamListings(toArray(listSnap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      setStudyGroups(toArray(groupSnap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      setMentors(toArray(mentorSnap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      setRooms(toArray(roomSnap).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    } catch (err) {
      
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill contact from profile
  useEffect(() => {
    if (userProfile?.phone) {
      setMaterialForm((f) => ({ ...f, contact: userProfile.phone }));
      setGroupForm((f) => ({ ...f, contact: userProfile.phone }));
      setMentorForm((f) => ({ ...f, contact: userProfile.phone }));
      setRoomForm((f) => ({ ...f, contact: userProfile.phone }));
    }
  }, [userProfile]);

  // ─── Join as Aspirant ────────────────────────────────────────────────────────
  const handleJoin = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    setJoining(true);
    await updateUserProfile(currentUser.uid, {
      examAspirant: true,
      primaryZone: "exam",
      targetExam: joinForm.targetExam,
      targetYear: joinForm.targetYear,
      examCity: joinForm.city,
      examLanguage: joinForm.language,
    });
    setJoining(false);
  };

  // Reset form when tab changes
  useEffect(() => {
    setShowPostForm(false);
    setSearchQuery("");
  }, [activeTab]);

  // ─── Filtering ──────────────────────────────────────────────────────────────

  const applyFilters = (items, { examKey = "examType", langKey = "language", includeSearch = true } = {}) => {
    return items.filter((item) => {
      if (activeExam !== "All" && item[examKey] !== activeExam) return false;
      if (activeLanguage !== "All" && item[langKey] !== activeLanguage) return false;
      if (includeSearch && searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const title = (item.title || "").toLowerCase();
        const desc = (item.description || "").toLowerCase();
        const name = (item.name || item.postedByName || "").toLowerCase();
        if (!title.includes(q) && !desc.includes(q) && !name.includes(q)) return false;
      }
      return true;
    });
  };

  const filteredMaterials = applyFilters(
    examListings.filter((i) => i.materialType !== "Current Affairs"),
  );
  const filteredCA = applyFilters(
    examListings.filter((i) => i.materialType === "Current Affairs"),
  );
  const filteredGroups = applyFilters(studyGroups);
  const filteredMentors = applyFilters(mentors, { langKey: "language", includeSearch: true });
  const filteredRooms = rooms.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (r.city || "").toLowerCase().includes(q) || (r.area || "").toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q);
  });

  // ─── Delete handlers ─────────────────────────────────────────────────────────

  const handleDeleteListing = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await remove(ref(db, `examListings/${id}`));
    setExamListings((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm("Delete this study group post?")) return;
    await remove(ref(db, `studyGroups/${id}`));
    setStudyGroups((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDeleteMentor = async (id) => {
    if (!window.confirm("Remove mentor profile?")) return;
    await remove(ref(db, `mentors/${id}`));
    setMentors((prev) => prev.filter((i) => i.id !== id));
  };

  const handleDeleteRoom = async (id) => {
    if (!window.confirm("Delete this room listing?")) return;
    await remove(ref(db, `examRoomSharing/${id}`));
    setRooms((prev) => prev.filter((i) => i.id !== id));
  };

  const handlePostRoom = async (e) => {
    e.preventDefault();
    if (!currentUser) return alert("Please login to post.");
    if (!roomForm.area.trim()) return alert("Area is required.");
    if (!roomForm.contact.trim()) return alert("Contact number is required.");
    setPosting(true);
    try {
      const payload = {
        ...roomForm,
        rent: Number(roomForm.rent) || 0,
        roommates: Number(roomForm.roommates) || 1,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
        createdAt: Date.now(),
      };
      const newRef = await push(ref(db, "examRoomSharing"), payload);
      setRooms((prev) => [{ id: newRef.key, ...payload }, ...prev]);
      setShowPostForm(false);
      setRoomForm({ city: "Pune", area: "", rent: "", type: "PG", furnished: "Furnished", roommates: "1", description: "", contact: userProfile?.phone || "" });
    } catch (err) {
      
      alert("Failed to post. Please try again.");
    }
    setPosting(false);
  };

  // ─── Post handlers ───────────────────────────────────────────────────────────

  const handlePostMaterial = async (e) => {
    e.preventDefault();
    if (!currentUser) return alert("Please login to post.");
    if (!materialForm.title.trim()) return alert("Title is required.");
    if (!materialForm.contact.trim()) return alert("Contact number is required.");
    setPosting(true);
    try {
      const payload = {
        ...materialForm,
        price: Number(materialForm.price) || 0,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
        college: userProfile?.college || "",
        createdAt: Date.now(),
      };
      const newRef = await push(ref(db, "examListings"), payload);
      const newItem = { id: newRef.key, ...payload };
      setExamListings((prev) => [newItem, ...prev]);
      setShowPostForm(false);
      setMaterialForm({
        materialType: "Book",
        title: "",
        examType: "MPSC",
        subject: "Polity",
        language: "Marathi",
        price: "",
        description: "",
        contact: userProfile?.phone || "",
        condition: "Good",
      });
    } catch (err) {
      
      alert("Failed to post. Please try again.");
    }
    setPosting(false);
  };

  const handlePostGroup = async (e) => {
    e.preventDefault();
    if (!currentUser) return alert("Please login to post.");
    if (!groupForm.description.trim()) return alert("Description is required.");
    if (!groupForm.contact.trim()) return alert("Contact number is required.");
    setPosting(true);
    try {
      const payload = {
        ...groupForm,
        membersNeeded: Number(groupForm.membersNeeded) || 2,
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
        createdAt: Date.now(),
      };
      const newRef = await push(ref(db, "studyGroups"), payload);
      const newItem = { id: newRef.key, ...payload };
      setStudyGroups((prev) => [newItem, ...prev]);
      setShowPostForm(false);
      setGroupForm({
        examType: "MPSC",
        language: "Marathi",
        location: "",
        schedule: "Flexible",
        membersNeeded: "2",
        description: "",
        contact: userProfile?.phone || "",
      });
    } catch (err) {
      
      alert("Failed to post. Please try again.");
    }
    setPosting(false);
  };

  const handlePostMentor = async (e) => {
    e.preventDefault();
    if (!currentUser) return alert("Please login to post.");
    if (!mentorForm.bio.trim()) return alert("Bio is required.");
    if (!mentorForm.contact.trim()) return alert("Contact number is required.");
    if (mentorForm.subjects.length === 0) return alert("Select at least one subject.");
    setPosting(true);
    try {
      const payload = {
        ...mentorForm,
        subjects: mentorForm.subjects,
        pricePerSession: Number(mentorForm.pricePerSession) || 0,
        name: userProfile?.name || currentUser.displayName || "Anonymous",
        postedBy: currentUser.uid,
        postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
        college: userProfile?.college || "",
        rating: 0,
        createdAt: Date.now(),
      };
      const newRef = await push(ref(db, "mentors"), payload);
      const newItem = { id: newRef.key, ...payload };
      setMentors((prev) => [newItem, ...prev]);
      setShowPostForm(false);
      setMentorForm({
        examType: "MPSC",
        yearCleared: "",
        subjects: [],
        mode: "Online",
        pricePerSession: "",
        bio: "",
        contact: userProfile?.phone || "",
      });
    } catch (err) {
      
      alert("Failed to post. Please try again.");
    }
    setPosting(false);
  };

  const toggleMentorSubject = (subject) => {
    setMentorForm((f) => ({
      ...f,
      subjects: f.subjects.includes(subject)
        ? f.subjects.filter((s) => s !== subject)
        : [...f.subjects, subject],
    }));
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  const tabs = [
    { key: "material", label: "Study Material", icon: <BookOpen size={15} /> },
    { key: "groups", label: "Study Groups", icon: <Users size={15} /> },
    { key: "mentors", label: "Mentors", icon: <GraduationCap size={15} /> },
    { key: "ca", label: "Current Affairs", icon: <Newspaper size={15} /> },
    { key: "rooms", label: "Room Sharing", icon: <Building2 size={15} /> },
  ];

  // ─── Join Gate Screen ────────────────────────────────────────────────────────
  if (currentUser && !isAspirant) {
    return (
      <div className="bg-gray-50 min-h-screen pb-24">
        <div className="bg-gradient-to-r from-orange-600 via-red-600 to-blue-900 pt-10 pb-8 px-4 text-white text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
          <Lock size={40} className="mx-auto mb-3 opacity-80" />
          <h1 className="text-2xl font-extrabold">Exam Prep Zone</h1>
          <p className="text-white/70 text-sm mt-1">Exclusive for Government Exam Aspirants</p>
        </div>
        <div className="px-4 py-6 max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-orange-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserPlus size={20} className="text-orange-500" />
              <h2 className="font-bold text-gray-800 text-lg">Join the Zone</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">Tell us about your exam preparation to get access. Only genuine aspirants allowed.</p>
            {!showJoinForm ? (
              <button onClick={() => setShowJoinForm(true)}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 rounded-xl text-sm">
                🎯 Register as Aspirant
              </button>
            ) : (
              <form onSubmit={handleJoin} className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Target Exam *</label>
                  <select value={joinForm.targetExam} onChange={e => setJoinForm(f => ({...f, targetExam: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-400">
                    {EXAM_TYPES.map(e => <option key={e}>{e}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Target Year *</label>
                  <select value={joinForm.targetYear} onChange={e => setJoinForm(f => ({...f, targetYear: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-400">
                    {[0,1,2,3].map(i => { const y = String(new Date().getFullYear() + i); return <option key={y}>{y}</option>; })}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Your City *</label>
                  <select value={joinForm.city} onChange={e => setJoinForm(f => ({...f, city: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-400">
                    {ROOM_CITIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 block mb-1">Preferred Language *</label>
                  <select value={joinForm.language} onChange={e => setJoinForm(f => ({...f, language: e.target.value}))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-orange-400">
                    {LANGUAGES.map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <p className="text-xs text-gray-400 bg-gray-50 rounded-xl p-3">
                  ⚠️ By joining, you confirm you are genuinely preparing for a government exam. False registrations may result in removal.
                </p>
                <button type="submit" disabled={joining}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 rounded-xl text-sm disabled:opacity-60">
                  {joining ? "Registering..." : "✅ Confirm & Join Exam Prep Zone"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-orange-600 via-red-600 to-blue-900 pt-10 pb-6 px-4 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-4 w-24 h-24 rounded-full bg-white/5 translate-y-1/2" />
        <div className="absolute top-4 left-1/3 w-16 h-16 rounded-full bg-yellow-400/10" />

        <div className="relative max-w-2xl mx-auto">
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1.5 rounded-full hover:bg-white/20 mb-2"><ArrowLeft size={20} className="text-white" /></button>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xl">🎯</span>
            <h1 className="text-2xl font-extrabold text-white tracking-tight">
              Exam Prep Zone
            </h1>
          </div>
          <p className="text-white/80 text-sm font-medium mb-1">
            MPSC · UPSC · Banking · SSC · Railway
          </p>
          <p className="text-white/60 text-xs">
            Study materials, groups & mentors for competitive exam aspirants
          </p>
          {isAdmin && (
            <div className="inline-flex items-center gap-1.5 bg-yellow-400/20 border border-yellow-400/40 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full mt-2">
              <Crown size={12} /> Admin Mode — Full Access
            </div>
          )}
        </div>
      </div>

      {/* ── Exam filter row ─────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-orange-600 via-red-600 to-blue-900 px-4 pb-3">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-max">
            {EXAM_FILTERS.map((exam) => (
              <button
                key={exam}
                onClick={() => setActiveExam(exam)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  activeExam === exam
                    ? "bg-white text-orange-600 font-bold shadow-sm"
                    : "bg-white/20 text-white hover:bg-white/30"
                }`}
              >
                {exam}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Language filter row ─────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-orange-500/90 via-red-500/90 to-blue-800/90 px-4 pb-4">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {LANGUAGE_FILTERS.map((lang) => (
            <button
              key={lang.value}
              onClick={() => setActiveLanguage(lang.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                activeLanguage === lang.value
                  ? "bg-white text-blue-700 font-bold shadow-sm"
                  : "bg-white/20 text-white hover:bg-white/30"
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab.key
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Content area ───────────────────────────────────────────────────── */}
      <div className="max-w-2xl mx-auto px-4 py-4">

        {/* Search bar */}
        <div className="relative mb-4">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={
              activeTab === "material"
                ? "Search study materials..."
                : activeTab === "groups"
                ? "Search study groups..."
                : activeTab === "mentors"
                ? "Search mentors..."
                : "Search current affairs..."
            }
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* ── TAB 1: Study Material ─────────────────────────────────────────── */}
        {activeTab === "material" && (
          <>
            {/* Post button */}
            {currentUser && (
              <button
                onClick={() => setShowPostForm((v) => !v)}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-2.5 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all"
              >
                {showPostForm ? <><ChevronUp size={16} /> Hide Form</> : <><Plus size={16} /> Post Study Material</>}
              </button>
            )}

            {/* Post form */}
            {showPostForm && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <BookOpen size={16} className="text-orange-500" />
                  Post Study Material
                </h2>
                <form onSubmit={handlePostMaterial} className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Material Type" required>
                      <select
                        className={selectCls}
                        value={materialForm.materialType}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, materialType: e.target.value }))}
                      >
                        {MATERIAL_TYPES.filter((t) => t !== "Current Affairs").map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Exam Type" required>
                      <select
                        className={selectCls}
                        value={materialForm.examType}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, examType: e.target.value }))}
                      >
                        {EXAM_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <FormField label="Title" required>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="e.g. Laxmikant Polity 6th Edition"
                      value={materialForm.title}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, title: e.target.value }))}
                      required
                    />
                  </FormField>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Subject">
                      <select
                        className={selectCls}
                        value={materialForm.subject}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, subject: e.target.value }))}
                      >
                        {SUBJECTS.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Language" required>
                      <select
                        className={selectCls}
                        value={materialForm.language}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, language: e.target.value }))}
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l}>{l}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Price (₹) — 0 for Free" required>
                      <input
                        type="number"
                        min="0"
                        className={inputCls}
                        placeholder="0"
                        value={materialForm.price}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, price: e.target.value }))}
                      />
                    </FormField>
                    <FormField label="Condition" required>
                      <select
                        className={selectCls}
                        value={materialForm.condition}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, condition: e.target.value }))}
                      >
                        {CONDITIONS.map((c) => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <FormField label="Description">
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={3}
                      placeholder="Describe the material, edition, highlights..."
                      value={materialForm.description}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </FormField>

                  <FormField label="WhatsApp Contact (10-digit)" required>
                    <input
                      type="tel"
                      className={inputCls}
                      placeholder="9876543210"
                      maxLength={10}
                      value={materialForm.contact}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, contact: e.target.value.replace(/\D/g, "") }))}
                      required
                    />
                  </FormField>

                  <button
                    type="submit"
                    disabled={posting}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-2.5 rounded-xl mt-1 disabled:opacity-60 transition-all hover:shadow-md"
                  >
                    {posting ? "Posting..." : "Post Material"}
                  </button>
                </form>
              </div>
            )}

            {/* Listings */}
            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMaterials.length === 0 ? (
              <EmptyState
                emoji="📚"
                title="No study materials yet"
                subtitle="Be the first to share books, notes or PYQs for this exam!"
                onPost={currentUser ? () => setShowPostForm(true) : null}
                postLabel="Post Study Material"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredMaterials.map((item) => (
                  <MaterialCard
                    key={item.id}
                    item={item}
                    currentUserId={currentUser?.uid}
                    onDelete={handleDeleteListing}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB 2: Study Groups ───────────────────────────────────────────── */}
        {activeTab === "groups" && (
          <>
            {currentUser && (
              <button
                onClick={() => setShowPostForm((v) => !v)}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-2.5 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all"
              >
                {showPostForm ? <><ChevronUp size={16} /> Hide Form</> : <><Plus size={16} /> Find Study Partner</>}
              </button>
            )}

            {showPostForm && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users size={16} className="text-blue-500" />
                  Post Study Group Request
                </h2>
                <form onSubmit={handlePostGroup} className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Exam Type" required>
                      <select
                        className={selectCls}
                        value={groupForm.examType}
                        onChange={(e) => setGroupForm((f) => ({ ...f, examType: e.target.value }))}
                      >
                        {EXAM_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Language" required>
                      <select
                        className={selectCls}
                        value={groupForm.language}
                        onChange={(e) => setGroupForm((f) => ({ ...f, language: e.target.value }))}
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l}>{l}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="City / Location" required>
                      <input
                        type="text"
                        className={inputCls}
                        placeholder="e.g. Pune, Nashik, Online"
                        value={groupForm.location}
                        onChange={(e) => setGroupForm((f) => ({ ...f, location: e.target.value }))}
                        required
                      />
                    </FormField>
                    <FormField label="Members Needed">
                      <input
                        type="number"
                        min="1"
                        max="10"
                        className={inputCls}
                        value={groupForm.membersNeeded}
                        onChange={(e) => setGroupForm((f) => ({ ...f, membersNeeded: e.target.value }))}
                      />
                    </FormField>
                  </div>

                  <FormField label="Study Schedule" required>
                    <select
                      className={selectCls}
                      value={groupForm.schedule}
                      onChange={(e) => setGroupForm((f) => ({ ...f, schedule: e.target.value }))}
                    >
                      {SCHEDULES.map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </FormField>

                  <FormField label="Description" required>
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={3}
                      placeholder="Topics to cover, study plan, requirements..."
                      value={groupForm.description}
                      onChange={(e) => setGroupForm((f) => ({ ...f, description: e.target.value }))}
                      required
                    />
                  </FormField>

                  <FormField label="WhatsApp Contact (10-digit)" required>
                    <input
                      type="tel"
                      className={inputCls}
                      placeholder="9876543210"
                      maxLength={10}
                      value={groupForm.contact}
                      onChange={(e) => setGroupForm((f) => ({ ...f, contact: e.target.value.replace(/\D/g, "") }))}
                      required
                    />
                  </FormField>

                  <button
                    type="submit"
                    disabled={posting}
                    className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold py-2.5 rounded-xl mt-1 disabled:opacity-60 transition-all hover:shadow-md"
                  >
                    {posting ? "Posting..." : "Post Study Group"}
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredGroups.length === 0 ? (
              <EmptyState
                emoji="👥"
                title="No study groups yet"
                subtitle="Connect with fellow aspirants — post your study group request!"
                onPost={currentUser ? () => setShowPostForm(true) : null}
                postLabel="Find Study Partner"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredGroups.map((item) => (
                  <StudyGroupCard
                    key={item.id}
                    item={item}
                    currentUserId={currentUser?.uid}
                    onDelete={handleDeleteGroup}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB 3: Mentors ────────────────────────────────────────────────── */}
        {activeTab === "mentors" && (
          <>
            {currentUser && (
              <button
                onClick={() => setShowPostForm((v) => !v)}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-semibold py-2.5 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all"
              >
                {showPostForm ? <><ChevronUp size={16} /> Hide Form</> : <><Plus size={16} /> Offer Mentorship</>}
              </button>
            )}

            {showPostForm && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <GraduationCap size={16} className="text-yellow-500" />
                  Offer Mentorship
                </h2>
                <form onSubmit={handlePostMentor} className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Exam Cleared" required>
                      <select
                        className={selectCls}
                        value={mentorForm.examType}
                        onChange={(e) => setMentorForm((f) => ({ ...f, examType: e.target.value }))}
                      >
                        {EXAM_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Year Cleared">
                      <input
                        type="number"
                        className={inputCls}
                        placeholder="e.g. 2023"
                        min="2000"
                        max={new Date().getFullYear()}
                        value={mentorForm.yearCleared}
                        onChange={(e) => setMentorForm((f) => ({ ...f, yearCleared: e.target.value }))}
                      />
                    </FormField>
                  </div>

                  <FormField label="Subjects You Teach" required>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {MENTOR_SUBJECTS.map((subject) => (
                        <button
                          type="button"
                          key={subject}
                          onClick={() => toggleMentorSubject(subject)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                            mentorForm.subjects.includes(subject)
                              ? "bg-orange-500 text-white border-orange-500"
                              : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"
                          }`}
                        >
                          {subject}
                        </button>
                      ))}
                    </div>
                  </FormField>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Mode" required>
                      <select
                        className={selectCls}
                        value={mentorForm.mode}
                        onChange={(e) => setMentorForm((f) => ({ ...f, mode: e.target.value }))}
                      >
                        {MENTOR_MODES.map((m) => (
                          <option key={m}>{m}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Price/Session (₹) — 0 for Free" required>
                      <input
                        type="number"
                        min="0"
                        className={inputCls}
                        placeholder="0"
                        value={mentorForm.pricePerSession}
                        onChange={(e) => setMentorForm((f) => ({ ...f, pricePerSession: e.target.value }))}
                      />
                    </FormField>
                  </div>

                  <FormField label={`Bio (max 200 chars) — ${mentorForm.bio.length}/200`} required>
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={3}
                      maxLength={200}
                      placeholder="Your preparation journey, approach, success tips..."
                      value={mentorForm.bio}
                      onChange={(e) => setMentorForm((f) => ({ ...f, bio: e.target.value.slice(0, 200) }))}
                      required
                    />
                  </FormField>

                  <FormField label="WhatsApp Contact (10-digit)" required>
                    <input
                      type="tel"
                      className={inputCls}
                      placeholder="9876543210"
                      maxLength={10}
                      value={mentorForm.contact}
                      onChange={(e) => setMentorForm((f) => ({ ...f, contact: e.target.value.replace(/\D/g, "") }))}
                      required
                    />
                  </FormField>

                  <button
                    type="submit"
                    disabled={posting}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-2.5 rounded-xl mt-1 disabled:opacity-60 transition-all hover:shadow-md"
                  >
                    {posting ? "Posting..." : "Offer Mentorship"}
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredMentors.length === 0 ? (
              <EmptyState
                emoji="🧑‍🏫"
                title="No mentors yet"
                subtitle="Cleared an exam? Help fellow aspirants by offering mentorship!"
                onPost={currentUser ? () => setShowPostForm(true) : null}
                postLabel="Offer Mentorship"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredMentors.map((item) => (
                  <MentorCard
                    key={item.id}
                    item={item}
                    currentUserId={currentUser?.uid}
                    onDelete={handleDeleteMentor}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB 4: Current Affairs ────────────────────────────────────────── */}
        {activeTab === "ca" && (
          <>
            {currentUser && (
              <button
                onClick={() => setShowPostForm((v) => !v)}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-red-500 to-pink-600 text-white font-semibold py-2.5 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all"
              >
                {showPostForm ? <><ChevronUp size={16} /> Hide Form</> : <><Plus size={16} /> Post Current Affairs Material</>}
              </button>
            )}

            {showPostForm && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Newspaper size={16} className="text-red-500" />
                  Post Current Affairs Material
                </h2>
                <form
                  onSubmit={(e) => {
                    // Use material form handler with materialType forced to Current Affairs
                    const prevType = materialForm.materialType;
                    setMaterialForm((f) => ({ ...f, materialType: "Current Affairs" }));
                    // Need to call submit with updated form — use a small trick
                    e.preventDefault();
                    if (!materialForm.title.trim()) return alert("Title is required.");
                    if (!materialForm.contact.trim()) return alert("Contact number is required.");
                    setPosting(true);
                    const payload = {
                      ...materialForm,
                      materialType: "Current Affairs",
                      price: Number(materialForm.price) || 0,
                      postedBy: currentUser.uid,
                      postedByName: userProfile?.name || currentUser.displayName || "Anonymous",
                      college: userProfile?.college || "",
                      createdAt: Date.now(),
                    };
                    push(ref(db, "examListings"), payload)
                      .then((newRef) => {
                        const newItem = { id: newRef.key, ...payload };
                        setExamListings((prev) => [newItem, ...prev]);
                        setShowPostForm(false);
                        setMaterialForm({
                          materialType: "Book",
                          title: "",
                          examType: "MPSC",
                          subject: "Current Affairs",
                          language: "Marathi",
                          price: "",
                          description: "",
                          contact: userProfile?.phone || "",
                          condition: "Good",
                        });
                        setPosting(false);
                      })
                      .catch((err) => {
                        
                        alert("Failed to post. Please try again.");
                        setMaterialForm((f) => ({ ...f, materialType: prevType }));
                        setPosting(false);
                      });
                  }}
                  className="flex flex-col gap-3"
                >
                  <FormField label="Title" required>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="e.g. March 2026 Monthly CA Compilation"
                      value={materialForm.title}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, title: e.target.value }))}
                      required
                    />
                  </FormField>

                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Exam Type" required>
                      <select
                        className={selectCls}
                        value={materialForm.examType}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, examType: e.target.value }))}
                      >
                        {EXAM_TYPES.map((t) => (
                          <option key={t}>{t}</option>
                        ))}
                      </select>
                    </FormField>
                    <FormField label="Language" required>
                      <select
                        className={selectCls}
                        value={materialForm.language}
                        onChange={(e) => setMaterialForm((f) => ({ ...f, language: e.target.value }))}
                      >
                        {LANGUAGES.map((l) => (
                          <option key={l}>{l}</option>
                        ))}
                      </select>
                    </FormField>
                  </div>

                  <FormField label="Price (₹) — 0 for Free">
                    <input
                      type="number"
                      min="0"
                      className={inputCls}
                      placeholder="0"
                      value={materialForm.price}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, price: e.target.value }))}
                    />
                  </FormField>

                  <FormField label="Description">
                    <textarea
                      className={`${inputCls} resize-none`}
                      rows={3}
                      placeholder="Topics covered, month/year, format (PDF/Notes)..."
                      value={materialForm.description}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, description: e.target.value }))}
                    />
                  </FormField>

                  <FormField label="WhatsApp Contact (10-digit)" required>
                    <input
                      type="tel"
                      className={inputCls}
                      placeholder="9876543210"
                      maxLength={10}
                      value={materialForm.contact}
                      onChange={(e) => setMaterialForm((f) => ({ ...f, contact: e.target.value.replace(/\D/g, "") }))}
                      required
                    />
                  </FormField>

                  <button
                    type="submit"
                    disabled={posting}
                    className="w-full bg-gradient-to-r from-red-500 to-pink-600 text-white font-bold py-2.5 rounded-xl mt-1 disabled:opacity-60 transition-all hover:shadow-md"
                  >
                    {posting ? "Posting..." : "Post Current Affairs"}
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredCA.length === 0 ? (
              <EmptyState
                emoji="📰"
                title="No current affairs material yet"
                subtitle="Share monthly CA compilations, newspapers summaries or MCQ sets!"
                onPost={currentUser ? () => setShowPostForm(true) : null}
                postLabel="Post Current Affairs"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredCA.map((item) => (
                  <CurrentAffairsCard
                    key={item.id}
                    item={item}
                    currentUserId={currentUser?.uid}
                    onDelete={handleDeleteListing}
                    isAdmin={isAdmin}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── TAB 5: Room Sharing ───────────────────────────────────────────── */}
        {activeTab === "rooms" && (
          <>
            {currentUser && isAspirant && (
              <button
                onClick={() => setShowPostForm((v) => !v)}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-semibold py-2.5 rounded-xl mb-4 shadow-sm hover:shadow-md transition-all"
              >
                {showPostForm ? <><ChevronUp size={16} /> Hide Form</> : <><Plus size={16} /> Post Room / PG Listing</>}
              </button>
            )}

            {showPostForm && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
                <h2 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Building2 size={16} className="text-teal-500" />
                  Post Room / PG near Exam Center
                </h2>
                <form onSubmit={handlePostRoom} className="flex flex-col gap-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="City" required>
                      <select className={selectCls} value={roomForm.city} onChange={e => setRoomForm(f => ({...f, city: e.target.value}))}>
                        {ROOM_CITIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Area / Locality" required>
                      <input type="text" className={inputCls} placeholder="e.g. Deccan, Kothrud" value={roomForm.area}
                        onChange={e => setRoomForm(f => ({...f, area: e.target.value}))} required />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Room Type" required>
                      <select className={selectCls} value={roomForm.type} onChange={e => setRoomForm(f => ({...f, type: e.target.value}))}>
                        {ROOM_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                    </FormField>
                    <FormField label="Furnished">
                      <select className={selectCls} value={roomForm.furnished} onChange={e => setRoomForm(f => ({...f, furnished: e.target.value}))}>
                        {FURNISHED_OPTIONS.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Monthly Rent (₹)" required>
                      <input type="number" min="0" className={inputCls} placeholder="3500" value={roomForm.rent}
                        onChange={e => setRoomForm(f => ({...f, rent: e.target.value}))} required />
                    </FormField>
                    <FormField label="Roommates Needed">
                      <input type="number" min="1" max="10" className={inputCls} value={roomForm.roommates}
                        onChange={e => setRoomForm(f => ({...f, roommates: e.target.value}))} />
                    </FormField>
                  </div>
                  <FormField label="Description">
                    <textarea className={`${inputCls} resize-none`} rows={3}
                      placeholder="Near which coaching, amenities, distance from exam center..."
                      value={roomForm.description} onChange={e => setRoomForm(f => ({...f, description: e.target.value}))} />
                  </FormField>
                  <FormField label="WhatsApp Contact (10-digit)" required>
                    <input type="tel" className={inputCls} placeholder="9876543210" maxLength={10}
                      value={roomForm.contact} onChange={e => setRoomForm(f => ({...f, contact: e.target.value.replace(/\D/g,"")}))} required />
                  </FormField>
                  <button type="submit" disabled={posting}
                    className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white font-bold py-2.5 rounded-xl mt-1 disabled:opacity-60">
                    {posting ? "Posting..." : "Post Room Listing"}
                  </button>
                </form>
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-16">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredRooms.length === 0 ? (
              <EmptyState
                emoji="🏠"
                title="No room listings yet"
                subtitle="Post your PG or room near exam centers in Pune, Delhi, Mumbai etc."
                onPost={currentUser && isAspirant ? () => setShowPostForm(true) : null}
                postLabel="Post Room Listing"
              />
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {filteredRooms.map((room) => (
                  <div key={room.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1.5 mb-1.5">
                          <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">{room.type}</span>
                          <span className="bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">{room.furnished}</span>
                          <span className="bg-blue-50 text-blue-600 text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                            <MapPin size={10} />{room.city}
                          </span>
                        </div>
                        <p className="font-bold text-gray-800 text-sm">{room.area}, {room.city}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-blue-700 font-bold text-base">₹{room.rent}<span className="text-xs font-normal text-gray-400">/mo</span></span>
                          {room.roommates > 0 && <span className="text-xs text-gray-500 flex items-center gap-1"><Users size={11} /> {room.roommates} roommate{room.roommates > 1 ? "s" : ""} needed</span>}
                        </div>
                        {room.description && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{room.description}</p>}
                        <p className="text-xs text-gray-400 mt-1">Posted by {room.postedByName} · {timeAgo(room.createdAt)}</p>
                      </div>
                      {(isAdmin || (currentUser?.uid === room.postedBy)) && (
                        <button onClick={() => handleDeleteRoom(room.id)}
                          className="p-1.5 rounded-full hover:bg-red-50 text-gray-300 hover:text-red-400 flex-shrink-0">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                    {room.contact && (
                      <button onClick={() => openWhatsApp(room.contact)}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 bg-green-500 text-white py-2 rounded-xl text-xs font-semibold">
                        <MessageCircle size={13} /> Contact on WhatsApp
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Login nudge for unauthenticated users */}
        {!currentUser && (
          <div className="mt-6 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-100 rounded-2xl p-4 text-center">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-orange-600">Login</span> to post study material, join study groups, or offer mentorship.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
